import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaGlobe, 
  FaShieldAlt, 
  FaEdit, 
  FaUserFriends,
  FaEnvelope,
  FaFlag
} from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import { useCommunity } from '../../contexts/CommunityContext';
import IdentityTags from './IdentityTags';
import ProfileForm from './ProfileForm';
import { 
  shouldDisplayIdentity, 
  getVisibleIdentity, 
  getDisplayName, 
  getDisplayAvatar 
} from '../../utils/identityHelpers';
import { flagContent } from '../../utils/moderationUtils';
import '../../styles/ProfileView.css';

/**
 * Component for displaying a user's full profile
 */
const ProfileView = ({ userId, onClose }) => {
  const { currentUser, getUserById, updateProfile } = useUser();
  const { getFilteredContent } = useCommunity();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  
  // Get the user to display
  const user = getUserById(userId);
  
  // Check if this is the current user's profile
  const isCurrentUser = currentUser && currentUser.id === userId;
  
  if (!user) {
    return (
      <div className="profile-view profile-view--empty">
        <h2>User not found</h2>
        <button className="profile-view__close-btn" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }
  
  // Get display name and avatar based on anonymity settings
  const displayName = getDisplayName(user);
  const displayAvatar = getDisplayAvatar(user);
  
  // Check which identity fields should be visible
  const showGender = shouldDisplayIdentity(user, 'genderIdentity', currentUser);
  const showOrientation = shouldDisplayIdentity(user, 'sexualOrientation', currentUser);
  const showPronouns = shouldDisplayIdentity(user, 'pronouns', currentUser);
  
  // Get visible identity values
  const visibleGender = getVisibleIdentity(user, 'genderIdentity', currentUser);
  const visibleOrientation = getVisibleIdentity(user, 'sexualOrientation', currentUser);
  const visiblePronouns = getVisibleIdentity(user, 'pronouns', currentUser);
  
  // Get user's content
  const userStories = getFilteredContent('stories').filter(
    story => story.author.id === userId
  );
  
  const userReviews = getFilteredContent('reviews').filter(
    review => review.author.id === userId
  );
  
  // Handle edit profile
  const handleEditProfile = () => {
    setIsEditing(true);
  };
  
  // Handle profile update
  const handleProfileUpdate = (updatedProfile) => {
    return updateProfile(updatedProfile)
      .then(() => {
        setIsEditing(false);
        return { success: true };
      });
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
  };
  
  // Handle report user
  const handleReportUser = () => {
    setShowReportModal(true);
  };
  
  // Handle submit report
  const handleSubmitReport = () => {
    if (!reportReason) return;
    
    flagContent({
      contentType: 'profile',
      contentId: userId,
      reporterId: currentUser.id,
      reason: reportReason,
      details: reportDetails
    });
    
    setShowReportModal(false);
    setReportReason('');
    setReportDetails('');
  };
  
  // If in edit mode, show the profile form
  if (isEditing) {
    return (
      <div className="profile-view profile-view--editing">
        <h2>Edit Profile</h2>
        <ProfileForm 
          initialData={user} 
          onSubmit={handleProfileUpdate}
          onCancel={handleCancelEdit}
        />
      </div>
    );
  }
  
  return (
    <div className="profile-view">
      {/* Header with close button */}
      <div className="profile-view__header">
        <h2 className="profile-view__title">Profile</h2>
        <button className="profile-view__close-btn" onClick={onClose}>
          Close
        </button>
      </div>
      
      {/* Profile header with avatar and name */}
      <div className="profile-view__profile-header">
        <div className="profile-view__avatar-container">
          <img 
            src={displayAvatar} 
            alt={`${displayName}'s avatar`} 
            className="profile-view__avatar" 
          />
          {user.isLocalQueer && (
            <span className="profile-view__badge profile-view__badge--local" title="Local Queer">LQ</span>
          )}
          {user.isSafetyVerified && (
            <span className="profile-view__badge profile-view__badge--verified" title="Safety Verified">
              <FaShieldAlt />
            </span>
          )}
        </div>
        
        <div className="profile-view__name-container">
          <h1 className="profile-view__name">{displayName}</h1>
          {showPronouns && visiblePronouns && (
            <p className="profile-view__pronouns">{visiblePronouns}</p>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="profile-view__actions">
          {isCurrentUser ? (
            <button 
              className="profile-view__action-btn profile-view__action-btn--edit"
              onClick={handleEditProfile}
            >
              <FaEdit /> Edit Profile
            </button>
          ) : (
            <>
              <button className="profile-view__action-btn profile-view__action-btn--connect">
                <FaUserFriends /> Connect
              </button>
              <button className="profile-view__action-btn profile-view__action-btn--message">
                <FaEnvelope /> Message
              </button>
              <button 
                className="profile-view__action-btn profile-view__action-btn--report"
                onClick={handleReportUser}
              >
                <FaFlag /> Report
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Profile content */}
      <div className="profile-view__content">
        {/* Basic info */}
        <div className="profile-view__section">
          <h3 className="profile-view__section-title">Basic Info</h3>
          
          <div className="profile-view__info-grid">
            <div className="profile-view__info-item">
              <FaMapMarkerAlt className="profile-view__info-icon" />
              <span className="profile-view__info-label">Location:</span>
              <span className="profile-view__info-value">{user.location}</span>
            </div>
            
            {user.languagesSpoken && (
              <div className="profile-view__info-item">
                <FaGlobe className="profile-view__info-icon" />
                <span className="profile-view__info-label">Languages:</span>
                <span className="profile-view__info-value">
                  {user.languagesSpoken.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Bio */}
        {user.bio && (
          <div className="profile-view__section">
            <h3 className="profile-view__section-title">About</h3>
            <p className="profile-view__bio">{user.bio}</p>
          </div>
        )}
        
        {/* Identity */}
        <div className="profile-view__section">
          <h3 className="profile-view__section-title">Identity</h3>
          
          <div className="profile-view__identity-container">
            {showGender && visibleGender && (
              <div className="profile-view__identity-item">
                <h4 className="profile-view__identity-title">Gender Identity</h4>
                <IdentityTags tags={visibleGender} type="gender" />
              </div>
            )}
            
            {showOrientation && visibleOrientation && (
              <div className="profile-view__identity-item">
                <h4 className="profile-view__identity-title">Sexual Orientation</h4>
                <IdentityTags tags={visibleOrientation} type="orientation" />
              </div>
            )}
            
            {user.travelStyle && (
              <div className="profile-view__identity-item">
                <h4 className="profile-view__identity-title">Travel Style</h4>
                <IdentityTags tags={user.travelStyle} type="travel" />
              </div>
            )}
          </div>
        </div>
        
        {/* Favorite destinations */}
        {user.favoriteDestinations && user.favoriteDestinations.length > 0 && (
          <div className="profile-view__section">
            <h3 className="profile-view__section-title">Favorite Destinations</h3>
            <div className="profile-view__destinations">
              {user.favoriteDestinations.map((destination, index) => (
                <div key={index} className="profile-view__destination">
                  {destination}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* User content */}
        {(userStories.length > 0 || userReviews.length > 0) && (
          <div className="profile-view__section">
            <h3 className="profile-view__section-title">Content</h3>
            
            {userStories.length > 0 && (
              <div className="profile-view__content-section">
                <h4 className="profile-view__content-title">Stories ({userStories.length})</h4>
                <div className="profile-view__content-list">
                  {userStories.slice(0, 3).map(story => (
                    <div key={story.id} className="profile-view__content-item">
                      <h5 className="profile-view__content-item-title">{story.title}</h5>
                      <p className="profile-view__content-item-excerpt">
                        {story.content.substring(0, 100)}...
                      </p>
                    </div>
                  ))}
                  {userStories.length > 3 && (
                    <div className="profile-view__content-more">
                      +{userStories.length - 3} more stories
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {userReviews.length > 0 && (
              <div className="profile-view__content-section">
                <h4 className="profile-view__content-title">Reviews ({userReviews.length})</h4>
                <div className="profile-view__content-list">
                  {userReviews.slice(0, 3).map(review => (
                    <div key={review.id} className="profile-view__content-item">
                      <h5 className="profile-view__content-item-title">{review.title}</h5>
                      <div className="profile-view__content-item-rating">
                        Rating: {review.rating}/5
                      </div>
                    </div>
                  ))}
                  {userReviews.length > 3 && (
                    <div className="profile-view__content-more">
                      +{userReviews.length - 3} more reviews
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Report modal */}
      {showReportModal && (
        <div className="profile-view__modal-overlay">
          <div className="profile-view__modal">
            <h3 className="profile-view__modal-title">Report User</h3>
            <p className="profile-view__modal-text">
              Please tell us why you're reporting this user. Your report will be reviewed by our moderation team.
            </p>
            
            <div className="profile-view__modal-field">
              <label htmlFor="reportReason" className="profile-view__modal-label">
                Reason for reporting
              </label>
              <select
                id="reportReason"
                className="profile-view__modal-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason</option>
                <option value="Hate speech or discrimination">Hate speech or discrimination</option>
                <option value="Harassment or bullying">Harassment or bullying</option>
                <option value="Inappropriate content">Inappropriate content</option>
                <option value="Misinformation">Misinformation</option>
                <option value="Spam">Spam</option>
                <option value="Safety concern">Safety concern</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="profile-view__modal-field">
              <label htmlFor="reportDetails" className="profile-view__modal-label">
                Additional details
              </label>
              <textarea
                id="reportDetails"
                className="profile-view__modal-textarea"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Please provide any additional details about your report..."
                rows={4}
              />
            </div>
            
            <div className="profile-view__modal-actions">
              <button 
                className="profile-view__modal-btn profile-view__modal-btn--cancel"
                onClick={() => setShowReportModal(false)}
              >
                Cancel
              </button>
              <button 
                className="profile-view__modal-btn profile-view__modal-btn--submit"
                onClick={handleSubmitReport}
                disabled={!reportReason}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ProfileView.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired
};

export default ProfileView;
